import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { ArrowRight, ArrowLeft, RotateCcw, Check } from "lucide-react";

const chapter = { subject: "Biology · Class 10", title: "Photosynthesis" };

const steps = [
  {
    key: "precheck",
    label: "Pre-check",
    n: "01",
    heading: "Attempt three questions — before you open the chapter.",
    subheading:
      "Failing here is the point. Your brain locks on to the answer when it later meets it in the notes.",
  },
  {
    key: "feynman",
    label: "Feynman Box",
    n: "02",
    heading: "Close the notes. Write what you understood — in your own words.",
    subheading:
      "The AI detects copied text and asks for a rephrase. Where your explanation breaks, that is your gap.",
  },
  {
    key: "reflect",
    label: "Reflection",
    n: "03",
    heading: "One thinking question. Two minutes. Private and ungraded.",
    subheading:
      "The metacognitive habit that separates a marks-scorer from a genuine learner.",
  },
];

const preQs = [
  { q: "Which gas do plants absorb during photosynthesis?", a: "carbon dioxide" },
  { q: "In which organelle does photosynthesis take place?", a: "chloroplast" },
  { q: "Name the green pigment that captures light.", a: "chlorophyll" },
];

function PreCheck() {
  const [values, setValues] = useState(["", "", ""]);
  const [submitted, setSubmitted] = useState(false);
  return (
    <div data-testid="focus-step-precheck">
      <div className="space-y-3 md:space-y-4">
        {preQs.map((q, i) => {
          const val = values[i];
          const correct = submitted && val.trim().toLowerCase() === q.a;
          return (
            <div
              key={i}
              className={`rounded-2xl bg-white-card border p-4 md:p-5 transition-colors ${
                correct ? "border-ink" : "border-line"
              }`}
            >
              <div className="text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-ink-muted font-semibold mb-2">
                Q{i + 1} · No hints, no notes
              </div>
              <div className="font-display text-base md:text-lg leading-snug text-ink">
                {q.q}
              </div>
              <div className="mt-3 flex items-center gap-3">
                <input
                  value={val}
                  onChange={(e) => {
                    const next = [...values];
                    next[i] = e.target.value;
                    setValues(next);
                  }}
                  placeholder="Type your best guess…"
                  className="w-full border-b border-line focus:border-ink text-ink placeholder:text-ink-muted py-2 outline-none text-[14px] md:text-[15px]"
                  data-testid={`focus-precheck-input-${i}`}
                />
                {submitted && (
                  <span className="text-[11px] uppercase tracking-wide font-semibold text-ink-muted whitespace-nowrap">
                    {correct ? "close enough" : "note taken"}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-5 md:mt-6 flex flex-wrap items-center gap-3">
        <button
          onClick={() => setSubmitted(true)}
          className="btn-pill btn-primary"
          data-testid="focus-precheck-submit"
        >
          Log my guesses <Check size={14} />
        </button>
        <button
          onClick={() => {
            setValues(["", "", ""]);
            setSubmitted(false);
          }}
          className="btn-pill btn-ghost"
          data-testid="focus-precheck-reset"
        >
          <RotateCcw size={12} /> reset
        </button>
        {submitted && (
          <span className="text-[13px] text-ink-soft">
            Good. Open the chapter with sharper attention.
          </span>
        )}
      </div>
    </div>
  );
}

const forbidden =
  "photosynthesis is the process by which green plants use sunlight to synthesize foods from carbon dioxide and water";

function Feynman() {
  const [text, setText] = useState("");
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const looksCopied = text
    .toLowerCase()
    .replace(/\s+/g, " ")
    .includes(forbidden.slice(0, 60));
  const original = wordCount >= 20 && !looksCopied;

  return (
    <div data-testid="focus-step-feynman">
      <div className="rounded-2xl bg-white-card border border-line p-4 md:p-6">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <div className="text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-ink-muted font-semibold">
            Feynman Box · own words only
          </div>
          <div className="text-[11px] text-ink-muted font-semibold">
            {wordCount} words
          </div>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder="Explain photosynthesis to a curious 10-year-old. From memory. Skip the textbook definition."
          className="w-full text-ink placeholder:text-ink-muted resize-none font-display text-lg md:text-xl leading-relaxed outline-none"
          data-testid="focus-feynman-textarea"
        />
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={looksCopied ? "copied" : original ? "ok" : "empty"}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="mt-4 border-l-2 border-accent pl-4 py-1.5"
          data-testid="focus-feynman-feedback"
        >
          {looksCopied && (
            <p className="text-accent text-[13px] font-semibold uppercase tracking-wide">
              Coach ↳ that looked like the textbook. Try again from memory.
            </p>
          )}
          {!looksCopied && original && (
            <p className="text-ink text-[14px] leading-relaxed">
              Coach ↳ where did your explanation feel weakest? That is exactly
              the paragraph to re-open.
            </p>
          )}
          {!original && !looksCopied && (
            <p className="text-ink-muted text-[13px] font-semibold uppercase tracking-wide">
              Coach ↳ keep going. Twenty words is the floor.
            </p>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

const prompts = [
  "Where did your explanation struggle most, and why?",
  "If you had to teach this to a 10-year-old, what analogy would you use?",
  "What is one thing you still don't fully understand about this topic?",
];

function Reflection() {
  const [idx, setIdx] = useState(0);
  const [answer, setAnswer] = useState("");
  const [saved, setSaved] = useState(false);
  return (
    <div data-testid="focus-step-reflect">
      <div className="rounded-2xl bg-white-card border border-line p-4 md:p-6">
        <div className="text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-ink-muted font-semibold mb-3">
          Reflection · private · ungraded
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="font-display text-lg md:text-2xl leading-snug text-ink"
          >
            {prompts[idx]}
          </motion.div>
        </AnimatePresence>
        <textarea
          value={answer}
          onChange={(e) => {
            setAnswer(e.target.value);
            setSaved(false);
          }}
          rows={4}
          placeholder="Write freely. Nobody grades this — not even the teacher."
          className="mt-4 w-full text-ink placeholder:text-ink-muted resize-none text-[14px] md:text-[15px] leading-relaxed outline-none border-t border-line pt-3"
          data-testid="focus-reflect-textarea"
        />
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          onClick={() => setIdx((idx + 1) % prompts.length)}
          className="btn-pill btn-ghost"
          data-testid="focus-reflect-shuffle"
        >
          Try another prompt <RotateCcw size={12} />
        </button>
        <button
          onClick={() => setSaved(true)}
          disabled={!answer.trim()}
          className="btn-pill btn-primary disabled:opacity-40"
          data-testid="focus-reflect-save"
        >
          Save reflection <Check size={14} />
        </button>
        {saved && (
          <span className="text-[13px] text-ink-soft">
            Saved to your Personal Learning Journal.
          </span>
        )}
      </div>
    </div>
  );
}

export default function FocusPreview() {
  const [step, setStep] = useState(0);
  return (
    <section
      id="focus-preview"
      className="relative bg-cream py-16 md:py-24"
      data-testid="focus-preview-section"
    >
      <div className="mx-auto max-w-[1200px] px-5 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
          <div className="text-[12px] md:text-[13px] uppercase tracking-[0.18em] text-ink-muted font-semibold">
            Interactive · ProBodh Focus
          </div>
          <h2 className="mt-3 md:mt-4 font-display text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-ink tracking-tight">
            Try the{" "}
            <span className="font-italic-serif text-accent">Feynman flow</span>.
            Three steps. No answers given away.
          </h2>
        </div>

        {/* Card frame */}
        <div className="rounded-2xl md:rounded-[28px] bg-off border border-line-soft p-4 md:p-8 lg:p-10 card-shadow">
          {/* Chapter header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5 md:mb-6">
            <div>
              <div className="text-[11px] md:text-[12px] uppercase tracking-[0.16em] text-ink/70 font-semibold">
                {chapter.subject}
              </div>
              <div className="font-display text-xl md:text-2xl text-ink mt-1">
                {chapter.title}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setStep(Math.max(0, step - 1))}
                disabled={step === 0}
                className="w-10 h-10 rounded-full bg-white-card/80 flex items-center justify-center text-ink hover:bg-white-card disabled:opacity-30 transition"
                aria-label="Previous step"
                data-testid="focus-prev"
              >
                <ArrowLeft size={16} />
              </button>
              <button
                onClick={() => setStep(Math.min(steps.length - 1, step + 1))}
                disabled={step === steps.length - 1}
                className="w-10 h-10 rounded-full bg-ink text-white-t flex items-center justify-center hover:bg-ink/80 disabled:opacity-30 transition"
                aria-label="Next step"
                data-testid="focus-next"
              >
                <ArrowRight size={16} />
              </button>
            </div>
          </div>

          {/* Step pills */}
          <div className="flex flex-wrap gap-2 mb-5 md:mb-6" data-testid="focus-steps">
            {steps.map((s, i) => {
              const isActive = i === step;
              return (
                <button
                  key={s.key}
                  onClick={() => setStep(i)}
                  className={`inline-flex items-center gap-2 px-3.5 md:px-4 py-2 rounded-full text-[12px] md:text-[13px] font-semibold border transition-all ${
                    isActive
                      ? "bg-ink text-white-t border-ink"
                      : "bg-white-card text-ink border-line hover:border-ink"
                  }`}
                  data-testid={`focus-step-nav-${s.key}`}
                >
                  <span className="opacity-60">{s.n}</span>
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Step heading */}
          <div className="mb-5 md:mb-6">
            <div className="font-display text-xl md:text-2xl leading-tight text-ink">
              {steps[step].heading}
            </div>
            <div className="mt-2 text-ink/75 text-[14px] md:text-[15px] leading-relaxed max-w-[62ch]">
              {steps[step].subheading}
            </div>
          </div>

          {/* Active step */}
          <AnimatePresence mode="wait">
            <motion.div
              key={steps[step].key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              {step === 0 && <PreCheck />}
              {step === 1 && <Feynman />}
              {step === 2 && <Reflection />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
